(() => {
  const CONFIG_SELECTOR = '[data-jrb-package-protection-config]';
  const TEMPLATE_ID = 'JrbPackageProtectionTemplate';
  const UI_SELECTOR = '[data-jrb-package-protection-ui]';
  const TOGGLE_SELECTOR = '[data-jrb-package-protection-toggle]';
  const PRIVATE_PROPERTY = '_jrb_package_protection';
  const FREE_ENTRIES_PROPERTY = '_free_entries_offer';

  class JRBPackageProtection {
    constructor(configElement) {
      this.cart = null;
      this.busy = false;
      this.syncTimer = null;
      this.bound = false;
      this.refreshTimers = [];
      this.memoryPreference = null;
      this.updateConfig(configElement);
    }

    updateConfig(configElement) {
      this.configElement = configElement;
      this.variantId = Number(configElement.dataset.variantId || 0);
      this.handle = configElement.dataset.handle || 'jrb-package-protection';
      this.sku = configElement.dataset.sku || 'JRB-PACKAGE-PROTECTION';
      this.formattedPrice = configElement.dataset.formattedPrice || '$5';
      this.enabled = configElement.dataset.enabled !== 'false';
      this.defaultSelected = configElement.dataset.defaultSelected === 'true';
      this.preferenceKey = `jrb-package-protection:${this.variantId}`;
    }

    getPreference() {
      try {
        return window.sessionStorage.getItem(this.preferenceKey);
      } catch (error) {
        return this.memoryPreference;
      }
    }

    setPreference(value) {
      this.memoryPreference = value;
      try {
        window.sessionStorage.setItem(this.preferenceKey, value);
      } catch (error) {
      }
    }

    init() {
      if (!this.variantId || this.bound) return;
      this.bound = true;
      document.addEventListener('change', (event) => this.onChange(event));
      document.addEventListener('click', (event) => this.onCheckoutClick(event), true);
      document.addEventListener('submit', (event) => this.onCheckoutSubmit(event), true);
      document.addEventListener('cart:refresh', (event) => this.onCartEvent(event));
      document.addEventListener('cart:item-added', (event) => this.onCartEvent(event));
      document.addEventListener('line-item:change:end', (event) => this.onCartEvent(event));
      document.addEventListener('shopify:section:load', () => { this.patchFreeEntriesController(); this.scheduleSync(); });
      window.addEventListener('pageshow', () => this.scheduleSync());
      void this.sync(true);
      window.setTimeout(() => this.patchFreeEntriesController(), 0);
    }

    getCartUrl() {
      return window.theme?.routes?.cart_url ? `${window.theme.routes.cart_url}.js` : '/cart.js';
    }

    isProtectionItem(item) {
      if (!item) return false;
      const props = item.properties || {};
      return Number(item.variant_id) === this.variantId || item.sku === this.sku || props[PRIVATE_PROPERTY] === 'true';
    }

    isFreeEntriesItem(item) {
      const props = item?.properties || {};
      return props[FREE_ENTRIES_PROPERTY] === 'true';
    }

    qualifyingItems(cart = this.cart) {
      if (!cart || !Array.isArray(cart.items)) return [];
      return cart.items.filter((item) => !this.isProtectionItem(item) && !this.isFreeEntriesItem(item) && item.requires_shipping === true && Number(item.quantity || 0) > 0);
    }

    protectionItems(cart = this.cart) {
      if (!cart || !Array.isArray(cart.items)) return [];
      return cart.items.filter((item) => this.isProtectionItem(item));
    }

    merchandiseSubtotal(cart = this.cart) {
      return this.qualifyingItems(cart).reduce((sum, item) => sum + Number(item.final_line_price || 0), 0);
    }

    formatMoney(cents) {
      if (typeof window.formatMoney === 'function') {
        const format = window.theme?.settings?.money_with_currency_format || '${{amount}}';
        return window.formatMoney(cents, format);
      }
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format(cents / 100);
    }

    async fetchCart() {
      const response = await fetch(this.getCartUrl(), { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Unable to load cart.');
      return response.json();
    }

    patchFreeEntriesController() {
      const controller = window.jrbFreeEntriesOfferController;
      if (!controller || controller.__jrbPackageProtectionPatched) return;
      const original = controller.getQualifyingLineItems.bind(controller);
      controller.getQualifyingLineItems = (cart) => original(cart).filter((item) => !this.isProtectionItem(item));
      controller.__jrbPackageProtectionPatched = true;
      if (typeof controller.scheduleSync === 'function') controller.scheduleSync(true);
    }

    async sync(forceFetch = false) {
      this.patchFreeEntriesController();
      try {
        if (forceFetch || !this.cart) this.cart = await this.fetchCart();
        const protection = this.protectionItems();
        const qualifying = this.qualifyingItems();

        if (!this.enabled) {
          if (protection.length) {
            await this.removeExtraProtectionLines(protection);
            return;
          }
          document.querySelectorAll(UI_SELECTOR).forEach((control) => control.remove());
          this.updateVisibleCartCount();
          this.updateCheckoutState();
          return;
        }

        if (protection.length > 1) {
          await this.removeExtraProtectionLines(protection.slice(1));
          return;
        }
        if (protection.length === 1 && Number(protection[0].quantity || 0) !== 1) {
          await this.changeLine(protection[0].key, 1);
          return;
        }
        if (protection.length && !qualifying.length) {
          await this.changeLine(protection[0].key, 0);
          return;
        }

        if (!protection.length && qualifying.length) {
          const preference = this.getPreference();
          const shouldAutoSelect = preference === 'on' || ((preference === null || preference === 'auto') && this.defaultSelected);
          if (shouldAutoSelect) {
            this.busy = true;
            try {
              await this.addProtection();
              this.cart = await this.fetchCart();
              if (preference === null) this.setPreference('auto');
              this.notifyRefresh();
            } finally {
              this.busy = false;
            }
            return;
          }
        }

        this.render();
      } catch (error) {
        this.setStatus(error.message || 'Package Protection could not be updated.', true);
      }
    }

    scheduleSync(cart = null) {
      if (cart && Array.isArray(cart.items)) this.cart = cart;
      window.clearTimeout(this.syncTimer);
      this.syncTimer = window.setTimeout(() => void this.sync(!cart), 80);
    }

    onCartEvent(event) {
      if (event?.detail?.source === 'jrb-package-protection') return;
      const cart = event?.detail?.cart && Array.isArray(event.detail.cart.items) ? event.detail.cart : null;
      this.scheduleSync(cart);
    }

    ensureControls() {
      const template = document.getElementById(TEMPLATE_ID);
      if (!template) return [];

      document.querySelectorAll(`${UI_SELECTOR}:not([data-jrb-package-protection-context])`).forEach((control) => {
        control.remove();
      });

      const insertControl = (parent, beforeNode, context) => {
        if (!parent) return;
        if (parent.querySelector(`${UI_SELECTOR}[data-jrb-package-protection-context="${context}"]`)) return;

        const fragment = template.content.cloneNode(true);
        const control = fragment.querySelector(UI_SELECTOR);
        if (!control) return;

        control.dataset.jrbPackageProtectionContext = context;

        const details = control.querySelector('.jrb-package-protection__details');
        const status = control.querySelector('[data-jrb-package-protection-status]');
        const toggle = control.querySelector(TOGGLE_SELECTOR);
        if (details?.id) details.id = `${details.id}-${context}`;
        if (status?.id) status.id = `${status.id}-${context}`;
        if (toggle) {
          const describedBy = [details?.id, status?.id].filter(Boolean).join(' ');
          if (describedBy) toggle.setAttribute('aria-describedby', describedBy);
        }

        if (beforeNode && beforeNode.parentElement === parent) {
          parent.insertBefore(fragment, beforeNode);
        } else {
          parent.appendChild(fragment);
        }
      };

      const drawerContent = document.querySelector('.cart-drawer .side-panel-content');
      if (drawerContent) {
        const drawerRecommendations = drawerContent.querySelector('.jrb-recommendations--cart');
        insertControl(drawerContent, drawerRecommendations, 'drawer');
      }

      const cartTotals = document.querySelector('.thb-cart-collaterals table.collaterals');
      if (cartTotals?.parentElement) {
        insertControl(cartTotals.parentElement, cartTotals, 'cart');
      }

      return Array.from(document.querySelectorAll(UI_SELECTOR));
    }

    render() {
      if (!this.enabled) {
        document.querySelectorAll(UI_SELECTOR).forEach((control) => control.remove());
        this.updateVisibleCartCount();
        this.updateCheckoutState();
        return;
      }

      const qualifying = this.qualifyingItems();
      const protection = this.protectionItems();
      const selected = protection.length > 0;
      const eligible = qualifying.length > 0;
      const subtotal = this.formatMoney(this.merchandiseSubtotal());
      const controls = this.ensureControls();

      controls.forEach((control) => {
        control.hidden = !eligible;
        control.classList.toggle('is-busy', this.busy);
        const toggle = control.querySelector(TOGGLE_SELECTOR);
        if (toggle) {
          toggle.checked = selected;
          toggle.disabled = this.busy || !eligible;
          toggle.setAttribute('aria-busy', this.busy ? 'true' : 'false');
        }
        const title = control.querySelector('[data-jrb-package-protection-title]');
        if (title) {
          title.textContent = control.dataset.jrbPackageProtectionContext === 'drawer'
            ? `Package Protection +${this.formattedPrice}`
            : `Protect your ${subtotal} order for ${this.formattedPrice}`;
        }
      });

      this.updateVisibleCartCount();
      this.decorateProtectionLines();
      this.updateCheckoutState();
    }

    updateVisibleCartCount() {
      if (!this.cart) return;
      const protectionQty = this.protectionItems().reduce((sum, item) => sum + Number(item.quantity || 0), 0);
      const count = Math.max(Number(this.cart.item_count || 0) - protectionQty, 0);
      document.querySelectorAll('#cart-drawer-toggle .thb-item-count, .cart-drawer .side-panel-header sup').forEach((node) => {
        if (count > 99 && node.matches('.thb-item-count')) return;
        node.textContent = String(count);
      });
    }

    decorateProtectionLines() {
      const hrefPart = `/products/${this.handle}`;
      document.querySelectorAll(`a[href*="${hrefPart}"]`).forEach((link) => {
        const line = link.closest('tr') || link.closest('.product-cart-item');
        if (!line) return;
        line.classList.add('jrb-package-protection-line');
        line.querySelectorAll('quantity-selector').forEach((qty) => { qty.style.display = 'none'; });
        const info = line.querySelector('.product-cart-item-info, .product-name > .product-cart-item > div:last-child');
        if (info && !info.querySelector('.jrb-package-protection-line__note')) {
          const note = document.createElement('span');
          note.className = 'jrb-package-protection-line__note';
          note.textContent = 'Optional package protection \u00b7 Qty 1';
          info.appendChild(note);
        }
      });
    }

    updateCheckoutState() {
      const invalid = this.protectionItems().length > 0 && this.qualifyingItems().length === 0;
      document.querySelectorAll('button[name="checkout"]').forEach((button) => {
        if (invalid) {
          button.dataset.jrbPackageProtectionDisabled = 'true';
          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');
        } else if (button.dataset.jrbPackageProtectionDisabled === 'true') {
          delete button.dataset.jrbPackageProtectionDisabled;
          const freeEntriesBlocked = document.documentElement.classList.contains('jrb-free-entries-checkout-blocked');
          button.disabled = freeEntriesBlocked;
          button.setAttribute('aria-disabled', freeEntriesBlocked ? 'true' : 'false');
        }
      });
      document.querySelectorAll('[data-free-entries-dynamic-checkout]').forEach((element) => {
        if (invalid) {
          element.dataset.jrbPackageProtectionHidden = 'true';
          element.hidden = true;
        } else if (element.dataset.jrbPackageProtectionHidden === 'true') {
          delete element.dataset.jrbPackageProtectionHidden;
          element.hidden = document.documentElement.classList.contains('jrb-free-entries-checkout-blocked');
        }
      });
    }

    async onChange(event) {
      const toggle = event.target.closest?.(TOGGLE_SELECTOR);
      if (!toggle || this.busy || !this.enabled) return;
      const shouldAdd = toggle.checked;
      toggle.checked = !shouldAdd;
      await this.setProtection(shouldAdd);
    }

    async setProtection(shouldAdd) {
      if (!this.enabled) return;
      this.busy = true;
      this.render();
      this.setStatus(shouldAdd ? 'Adding Package Protection\u2026' : 'Removing Package Protection\u2026');
      try {
        if (shouldAdd) {
          if (!this.qualifyingItems().length) throw new Error('Add qualifying shipped merchandise before selecting Package Protection.');
          await this.addProtection();
        } else {
          const item = this.protectionItems()[0];
          if (item) await this.changeLine(item.key, 0, false);
        }
        this.cart = await this.fetchCart();
        this.setPreference(shouldAdd ? 'on' : 'off');
        this.setStatus(shouldAdd ? 'Package Protection added.' : 'Package Protection removed.');
        this.notifyRefresh();
      } catch (error) {
        this.setStatus(error.message || 'Package Protection could not be updated.', true);
      } finally {
        this.busy = false;
        this.render();
      }
    }

    async addProtection() {
      if (!this.enabled) return;
      if (this.protectionItems().length) return;
      const url = window.theme?.routes?.cart_add_url ? `${window.theme.routes.cart_add_url}.js` : '/cart/add.js';
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: this.variantId, quantity: 1, properties: { [PRIVATE_PROPERTY]: 'true' } }] })
      });
      if (!response.ok) throw new Error('Unable to add Package Protection.');
      await response.json();
    }

    async changeLine(id, quantity, notify = true) {
      const url = window.theme?.routes?.cart_change_url || '/cart/change.js';
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id, quantity })
      });
      if (!response.ok) throw new Error('Unable to update Package Protection.');
      const cart = await response.json();
      this.cart = cart;
      if (notify) this.notifyRefresh();
      return cart;
    }

    async removeExtraProtectionLines(items) {
      for (const item of items) await this.changeLine(item.key, 0, false);
      this.cart = await this.fetchCart();
      this.notifyRefresh();
    }

    notifyRefresh() {
      this.refreshTimers.forEach((timer) => window.clearTimeout(timer));
      this.refreshTimers = [120, 350, 800].map((delay) => window.setTimeout(() => {
        this.render();
        this.patchFreeEntriesController();
      }, delay));
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { cart: this.cart, source: 'jrb-package-protection' }
      }));
    }

    setStatus(message, isError = false) {
      document.querySelectorAll('[data-jrb-package-protection-status]').forEach((node) => {
        node.textContent = message || '';
        node.dataset.error = isError ? 'true' : 'false';
      });
    }

    isCheckoutInvalid() {
      return this.protectionItems().length > 0 && this.qualifyingItems().length === 0;
    }

    onCheckoutClick(event) {
      const button = event.target.closest?.('button[name="checkout"]');
      if (!button || !this.isCheckoutInvalid()) return;
      event.preventDefault();
      event.stopPropagation();
      this.setStatus('Add qualifying shipped merchandise before checkout.', true);
    }

    onCheckoutSubmit(event) {
      if (!event.submitter?.matches?.('button[name="checkout"]') || !this.isCheckoutInvalid()) return;
      event.preventDefault();
      event.stopPropagation();
      this.setStatus('Add qualifying shipped merchandise before checkout.', true);
    }
  }

  const mount = () => {
    const config = document.querySelector(CONFIG_SELECTOR);
    if (!config) return;
    if (!window.jrbPackageProtectionController) {
      window.jrbPackageProtectionController = new JRBPackageProtection(config);
      window.jrbPackageProtectionController.init();
    } else {
      window.jrbPackageProtectionController.updateConfig(config);
      window.jrbPackageProtectionController.scheduleSync();
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount);
  else mount();
  document.addEventListener('shopify:section:load', mount);
})();
